import { apiAgent } from "@/network/axiosAgent";
import {
  mapApiFileToSwivuFile,
  mapFileSourceToApi,
  SwivuFileApiResponse,
  SwivuFileListApiResponse,
  SwivuFileListPage,
  SwivuFile,
} from "@/react/components/entityViews/files/types/swivuFile";
import type { FileListFilters } from "./files.query-keys";

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

/**
 * Payload for POST /file/create.
 * Requires document, board_id, and exactly one entity FK.
 * Optional fields mirror SwivuFileUpdatePayload so callers share the same shape.
 */
export interface SwivuFileCreatePayload {
  document: number;
  board_id: number;
  task?: number;
  resource?: number;
  client?: number;
  location?: number;
  name?: string;
  order?: number;
  description?: string | null;
  is_annotated?: boolean;
}

/** Create a new Swivu file record linked to an entity. Returns the full SwivuFile. */
export async function createFile(payload: SwivuFileCreatePayload): Promise<SwivuFile> {
  const { data } = await apiAgent.post<SwivuFileApiResponse>("/file/create", payload);
  return mapApiFileToSwivuFile(data);
}

// ---------------------------------------------------------------------------
// Single-file fetch
// ---------------------------------------------------------------------------

export type FileListEntityType = "task" | "resource" | "client" | "location";

export interface GetFileByIdParams {
  fileId: number;
  boardId: number;
  entityType: FileListEntityType;
  entityId: number;
}

/**
 * Resolve a Swivu file by ID via entity-scoped GET /file/list.
 *
 * There is no GET /file/{id} endpoint — callers must scope by board + entity
 * and locate the record in the paginated list.
 *
 * Caps how many pages we scan so a missing / deep file fails fast instead of
 * hanging UI loaders for huge CompanyCam-imported galleries.
 */
export async function getFileById(params: GetFileByIdParams): Promise<SwivuFile> {
  const limit = 100;
  const maxPages = 20; // 2,000 files max
  let offset = 0;
  let count = Infinity;
  let pages = 0;

  while (offset < count && pages < maxPages) {
    const page = await getFileList({
      board_id: params.boardId,
      [params.entityType]: params.entityId,
      limit,
      offset,
    });
    pages += 1;
    count = page.count;
    const found = page.results.find((file) => file.id === params.fileId);
    if (found) return found;
    if (page.results.length === 0) break;
    offset += limit;
  }

  throw new Error(`File ${params.fileId} not found on ${params.entityType} ${params.entityId}`);
}

export interface GetFileListParams extends FileListFilters {
  board_id: number;
  /** Filter by the user who created the files. */
  creator_id?: number;
  /** Exactly one entity filter is required by the API (unless creator_id is used) */
  task?: number;
  resource?: number;
  client?: number;
  location?: number;
  limit?: number;
  offset?: number;
}

/**
 * Fetch a single page of Swivu files.
 *
 * Transforms the snake_case API payload into camelCase `SwivuFile` objects so
 * the rest of the app never touches the raw response shape.
 */
export async function getFileList(params: GetFileListParams): Promise<SwivuFileListPage> {
  const { data } = await apiAgent.get<SwivuFileListApiResponse>("/file/list", {
    params: {
      board_id: params.board_id,
      ...(params.creator_id !== undefined && { creator_id: params.creator_id }),
      ...(params.task !== undefined && { task: params.task }),
      ...(params.resource !== undefined && { resource: params.resource }),
      ...(params.client !== undefined && { client: params.client }),
      ...(params.location !== undefined && { location: params.location }),
      ...(params.source !== undefined && { source: mapFileSourceToApi(params.source) }),
      ...(params.fileType !== undefined && { file_type: params.fileType }),
      ...(params.search !== undefined && params.search !== "" && { search: params.search }),
      ...(params.uploadedAfter !== undefined && { uploaded_after: params.uploadedAfter }),
      ...(params.uploadedBefore !== undefined && { uploaded_before: params.uploadedBefore }),
      ...(params.ordering !== undefined && { ordering: params.ordering }),
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    },
  });

  return {
    count: data.count,
    results: data.results.map(mapApiFileToSwivuFile),
  };
}

/**
 * Delete a Swivu file by ID.
 */
export async function deleteFileById(fileId: number): Promise<void> {
  await apiAgent.delete(`/file/${fileId}/delete`);
}

/**
 * Partial update payload for PATCH /file/{id}/update.
 * All fields are optional — only supplied fields are updated server-side.
 */
export interface SwivuFileUpdatePayload {
  description?: string | null;
  is_annotated?: boolean;
  name?: string;
  order?: number;
  document?: number;
  can_edit_grant?: number;
  can_view_grant?: number;
}

/**
 * Partially update a Swivu file record.
 * Returns the full updated SwivuFile (server round-trip so the cache stays accurate).
 */
export async function updateFileById(
  fileId: number,
  payload: SwivuFileUpdatePayload,
): Promise<SwivuFile> {
  const { data } = await apiAgent.patch<SwivuFileApiResponse>(
    `/file/${fileId}/update`,
    payload,
  );
  return mapApiFileToSwivuFile(data);
}
